import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import fetchData from '../service/apiService'
import centroidsData from '../components/charts/map/mapData/centroids.json'
import worldBoundariesData from '../components/charts/map/mapData/World-including-disputed.json'

const ongoingRequests = {}
export const fetchAndSetBoundaries = createAsyncThunk(
  'common/fetchAndSetBoundaries',
  async ({ endpoint, headers }, { getState, dispatch }) => {
    if (ongoingRequests['countryBoundaries']) {
      return ongoingRequests['countryBoundaries'].then(
        () => getState().common['countryBoundaries']
      )
    }

    const existingData = getState().common['countryBoundaries']
    if (existingData && existingData.features && existingData.features.length) {
      return existingData
    }
    try {
      const requestPromise = fetchData(endpoint, 'GET', null, headers)
      ongoingRequests['countryBoundaries'] = requestPromise
      const data = await requestPromise
      dispatch(setCountryBoundaries({ data }))
      return data
    } catch (error) {
      console.error('Error fetching data:', error)
      dispatch(setCountryBoundaries({ data: worldBoundariesData }))
      return worldBoundariesData
    } finally {
      // Remove the ongoing request promise when it's done
      delete ongoingRequests['countryBoundaries']
    }
  }
)

export const fetchAndSetCentroids = createAsyncThunk(
  'common/fetchAndSetCentroids',
  async ({ endpoint, headers }, { getState, dispatch }) => {
    if (ongoingRequests['countryCentroids']) {
      return ongoingRequests['countryCentroids'].then(
        () => getState().common['countryCentroids']
      )
    }

    const existingData = getState().common['countryCentroids']
    if (existingData && Object.keys(existingData).length) {
      return existingData
    }

    try {
      const requestPromise = fetchData(endpoint, 'GET', null, headers)
      ongoingRequests['countryCentroids'] = requestPromise

      const data = await requestPromise
      dispatch(setCountryCentroids({ data }))
      return data
    } catch (error) {
      console.error('Error fetching centroids:', error)
      dispatch(setCountryCentroids({ data: centroidsData }))
      return centroidsData
    } finally {
      delete ongoingRequests['countryCentroids']
    }
  }
)

export const fetchAndSetData = createAsyncThunk(
  'common/fetchAndSet',
  async ({ endpoint, key, headers }, { getState, dispatch }) => {
    if (ongoingRequests[key]) {
      return ongoingRequests[key].then(() => getState().common[key])
    }

    const existingData = getState().common[key]
    if (existingData) {
      return existingData
    }

    try {
      const requestPromise = fetchData(endpoint, 'GET', null, headers)
      ongoingRequests[key] = requestPromise

      const data = await requestPromise
      dispatch(setData({ key, data }))

      return data
    } catch (error) {
      console.error('Error fetching data:', error)
      dispatch(
        setData({
          key,
          data: [],
        })
      )
      throw error
    } finally {
      // Remove the ongoing request promise when it's done
      delete ongoingRequests[key]
    }
  }
)

export const fetchAndSetPostMethodData = createAsyncThunk(
  'common/fetchAndSetPostMethod',
  async ({ endpointData, key }, { getState, dispatch }) => {
    if (ongoingRequests[key]) {
      return ongoingRequests[key].then(() => getState().common[key])
    }

    const existingData = getState().common[key]
    if (existingData) {
      return existingData
    }

    try {
      const requestPromise = fetchData(
        endpointData.endpoint,
        endpointData.method,
        endpointData.data,
        endpointData.headers
      )
      ongoingRequests[key] = requestPromise

      const data = await requestPromise
      dispatch(setData({ key, data }))

      return data
    } catch (error) {
      console.error('Error fetching data:', error)
      throw error
    } finally {
      // Remove the ongoing request promise when it's done
      delete ongoingRequests[key]
    }
  }
)

const commonSlice = createSlice({
  name: 'common',
  initialState: {
    countryBoundaries: {},
    countryCentroids: {},
    endpointList: [],
    pageName: '',
    apiKeyName: '',
    apiKeyValue: '',
    hierarchyData: [],
    countryList: [],
    grouping: {},
    featureProps: {},
    selectedSidebarFeatured: {},
    selectedEconomiesFeatured: ['WLD'],
  },
  reducers: {
    setData: (state, action) => {
      const { key, data } = action.payload
      state[key] = data
    },
    setCountryBoundaries: (state, action) => {
      state.countryBoundaries = action.payload?.data
    },
    setCountryCentroids: (state, action) => {
      state.countryCentroids = action.payload.data
    },
    setEndpointList: (state, action) => {
      state.endpointList = action.payload
    },
    setPageName: (state, action) => {
      state.pageName = action.payload
    },
    setAPIKeyName: (state, action) => {
      state.apiKeyName = action.payload
    },
    setAPIKeyValue: (state, action) => {
      state.apiKeyValue = action.payload
    },
    setHierarchyData: (state, action) => {
      state.hierarchyData = action.payload
    },
    setCountryList: (state, action) => {
      state.countryList = action.payload
    },
    setGrouping: (state, action) => {
      state.grouping = action.payload
    },
    setFeatureProps: (state, action) => {
      state.featureProps = action.payload
    },
    setSelectedSidebarFeatured: (state, action) => {
      state.selectedSidebarFeatured = action.payload
    },
    setSelectedEconomiesFeatured: (state, action) => {
      state.selectedEconomiesFeatured = action.payload
    },
    addEconomyFeatured: (state, action) => {
      state.selectedEconomiesFeatured.push(action.payload)
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAndSetData.fulfilled, (state, action) => {
      const { key } = action.meta.arg
      state[key] = action.payload
    })
    builder.addCase(fetchAndSetPostMethodData.fulfilled, (state, action) => {
      const { key } = action.meta.arg
      state[key] = action.payload
    })
  },
})

export const {
  setData,
  setCountryBoundaries,
  setCountryCentroids,
  setEndpointList,
  setPageName,
  setAPIKeyName,
  setAPIKeyValue,
  setHierarchyData,
  setCountryList,
  setGrouping,
  setFeatureProps,
  setSelectedSidebarFeatured,
  setSelectedEconomiesFeatured,
  addEconomyFeatured,
} = commonSlice.actions

export default commonSlice.reducer
